import { Component, computed, Injector, signal, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { TrendOpenAIAPIService } from '@trendbuild/trend-openai-api';
import { AbstractComponent, Channel, ChannelService, Integration, IntegrationService, OpenAIAssistant, OpenAIAssistantInput, OpenAIIntegration, openAIAssistantNeedsSetup } from 'lib-trend-core';
import { finalize, takeUntil } from 'rxjs';

type Step = { title: string; subtitle: string };
export type ChannelRef = { _id: string; name: string; type: string };
type ModelOption = { label: string; value: string };

/**
 * Chat-model families offered to the customer. Dated snapshots (`gpt-4o-2024-08-06`) and
 * special-purpose variants (realtime, audio, search, transcribe, tts, pro) are left out.
 */
const CHAT_MODEL_PATTERN = /^gpt-(5(\.\d+)?|4\.1|4o)(-mini|-nano)?$/;

/** Shown when GET /v1/models fails (invalid key, network) so the form is never left empty. */
const FALLBACK_MODELS: string[] = ['gpt-5.4', 'gpt-5.2', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini'];

/**
 * Create / edit an OpenAI agent.
 *
 * Since the OpenAI Assistants API shutdown (2026-08-26) the agent (name, model,
 * instructions, channels) is stored in `integration.metadata.assistants` through
 * trendmulti-api — nothing is created on OpenAI's side. OpenAI is only called to list the
 * models the customer's project can use.
 */
@Component({
    selector: 'app-agent-openai-assistant-form',
    templateUrl: './agent-openai-assistant-form.component.html',
    styleUrls: ['./agent-openai-assistant-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentOpenAIAssistantFormComponent extends AbstractComponent {
  private readonly trendOpenAIAPIService: TrendOpenAIAPIService = new TrendOpenAIAPIService();

  public assistant: OpenAIAssistant;
  public integration: Integration;
  public currentAssistantId: string;
  public integrationId: string;

  public channelRef: ChannelRef[] = [];
  public listChannel: Channel[] = [];

  public models: ModelOption[] = [];
  public modelsLoading = false;
  public modelsFromFallback = false;

  /** Legacy agent created on the retired Assistants API: model/instructions must be filled again. */
  public needsSetup = false;

  stepIndex = signal(0);

  steps: Step[] = [
    { title: this.t('agents.openaiAssistantForm.step1Title'), subtitle: this.t('agents.openaiAssistantForm.step1Subtitle') },
    { title: this.t('agents.openaiAssistantForm.step2Title'), subtitle: this.t('agents.openaiAssistantForm.step2Subtitle') },
    { title: this.t('agents.openaiAssistantForm.step3Title'), subtitle: this.t('agents.openaiAssistantForm.step3Subtitle') },
  ];

  breadcrumb = [
    { href: '/agents/openai', label: this.t('agents.openaiAssistantForm.breadcrumbIntegrations') },
    { label: this.t('agents.openaiAssistantForm.breadcrumbEdit') },
  ];

  /** Form controls that belong to each step — validated before the step can be left. */
  private readonly stepFields: string[][] = [['name', 'model', 'description'], ['instructions'], ['channels']];

  constructor(
    injector: Injector,
    private integrationService: IntegrationService,
    private channelService: ChannelService,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.integrationId = this.getParam('id');
    this.currentAssistantId = this.getParam('assistantId');

    if (!this.currentAssistantId) {
      this.isNew = true;
      this.breadcrumb[1].label = this.t('agents.openaiAssistantForm.breadcrumbCreate');
    } else {
      this.loadingContent = true;
    }

    this.getIntegrationById(this.integrationId);
    this.loadChannels();
  }

  getIntegrationById(idIntegration: string) {
    this.integrationService.getById(idIntegration)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingContent = false;
        })
      )
      .subscribe({
        next: (integration: Integration) => {
          this.integration = integration;
          if (this.currentAssistantId) {
            this.loadAssistantFromIntegration();
          }
          this.loadModels();
        },
        error: (err) => {
          this.alertService.error(err.error.message);
        }
      });
  }

  /** The agent is an entry of integration.metadata.assistants — no remote call needed. */
  private loadAssistantFromIntegration(): void {
    const assistants = (<OpenAIIntegration>this.integration?.metadata)?.assistants ?? [];
    this.assistant = assistants.find((a: OpenAIAssistant) => a.id === this.currentAssistantId);

    if (!this.assistant) {
      this.alertService.error(this.t('agents.openaiAssistantForm.loadAssistantError'));
      this.router.navigate([`/agents/openai/${this.integrationId}/assistants`]);
      return;
    }

    this.needsSetup = openAIAssistantNeedsSetup(this.assistant);
    this.channelRef = this.assistant.channels ?? [];

    this.formGroup.patchValue({
      name: this.assistant.name ?? '',
      model: this.assistant.model ?? '',
      description: this.assistant.description ?? '',
      instructions: this.assistant.instructions ?? '',
      channels: this.assistant.channels?.map((c: any) => c._id) || [],
    }, { emitEvent: false });
  }

  /**
   * Models the customer's OpenAI project can use, newest first. Falls back to a curated list
   * when the call fails so the form never blocks. The model already configured on the agent
   * is always kept selectable.
   */
  async loadModels(): Promise<void> {
    const metadata = <OpenAIIntegration>this.integration?.metadata;
    this.modelsLoading = true;
    this.modelsFromFallback = false;

    let ids: string[] = [];
    try {
      const result = await this.trendOpenAIAPIService.listModels({
        apiKey: metadata?.token,
        organization: metadata?.organization || undefined,
        project: metadata?.project || undefined,
      });
      ids = (result?.data ?? []).map(m => m.id).filter(id => CHAT_MODEL_PATTERN.test(id));
    } catch {
      ids = [];
    }

    if (ids.length === 0) {
      ids = [...FALLBACK_MODELS];
      this.modelsFromFallback = true;
    }

    const current: string = this.formGroup.get('model')?.value || '';
    if (current && !ids.includes(current)) {
      ids.push(current);
    }

    this.models = ids
      .sort((a, b) => this.modelRank(b) - this.modelRank(a) || a.localeCompare(b))
      .map(id => ({ label: id, value: id }));
    this.modelsLoading = false;
  }

  /** Higher = newer/larger: gpt-5.4 > gpt-5.4-mini > gpt-5.2 > gpt-5 > gpt-4.1 > gpt-4o. */
  private modelRank(id: string): number {
    const match = /^gpt-(5(?:\.(\d+))?|4\.1|4o)(-mini|-nano)?$/.exec(id);
    if (!match) return -1;
    const family = match[1];
    const major = family.startsWith('5') ? 5 : family === '4.1' ? 4.1 : 4;
    const minor = family.startsWith('5') ? Number(match[2] ?? 0) : 0;
    const size = match[3] === '-nano' ? 0 : match[3] === '-mini' ? 1 : 2;
    return major * 1000 + minor * 10 + size;
  }

  setupForm(): void {
    this.formGroup = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(250)]],
      model: ['', [Validators.required]],
      description: ['', [Validators.maxLength(500)]],
      instructions: ['', [Validators.required]],
      channels: [[]],
    });
  }

  loadChannels() {
    this.channelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels) => {
          this.listChannel = channels;
        },
        error: () => {
          this.alertService.error(this.t('agents.openaiAssistantForm.loadChannelsError'));
        },
      });
  }

  /** Marks the step's controls as touched (shows `.is-invalid` feedback) and tells whether they are valid. */
  private validateStep(index: number): boolean {
    const fields = this.stepFields[index] ?? [];
    fields.forEach(field => this.formGroup.get(field)?.markAsTouched());
    return fields.every(field => !this.formGroup.get(field)?.invalid);
  }

  /** First step (from 0 to `upTo` inclusive) that fails validation, or -1 when all pass. */
  private firstInvalidStep(upTo: number): number {
    for (let i = 0; i <= upTo; i++) {
      if (!this.validateStep(i)) return i;
    }
    return -1;
  }

  /** Header click: going back is always allowed; going forward requires the steps in between to be valid. */
  goToStep(index: number): void {
    const current = this.clampedIndex();
    if (index <= current) {
      this.stepIndex.set(index);
      return;
    }
    const invalid = this.firstInvalidStep(index - 1);
    this.stepIndex.set(invalid >= 0 ? invalid : index);
  }

  /** Enter key / primary button of the current step. */
  submitCurrentStep(): void {
    if (this.clampedIndex() === 0) {
      this.nextStep();
    } else {
      this.saveAndNextStep();
    }
  }

  nextStep(): void {
    if (!this.validateStep(this.clampedIndex())) return;
    const maxIndex = Math.max(this.steps.length - 1, 0);
    this.stepIndex.update(index => Math.min(index + 1, maxIndex));
  }

  async saveAndNextStep(): Promise<void> {
    if (this.loading) return;

    // Everything is persisted together, so every step up to the current one must be valid;
    // otherwise jump to the first offending step with its feedback visible.
    const invalid = this.firstInvalidStep(this.clampedIndex());
    if (invalid >= 0) {
      this.stepIndex.set(invalid);
      return;
    }

    const payload = this.formGroup.getRawValue();
    const channels: ChannelRef[] = Array.isArray(payload.channels)
      ? payload.channels
        .map((idChannel: string) => this.listChannel.find(c => c._id === idChannel))
        .filter((channel: Channel | undefined) => !!channel)
        .map((channel: Channel) => ({ _id: channel._id, name: channel.name, type: channel.type }))
      : [];

    const input: OpenAIAssistantInput = {
      name: payload.name,
      model: payload.model,
      description: payload.description || undefined,
      instructions: payload.instructions,
      channels,
    };

    try {
      this.loading = true;

      if (this.isNew) {
        const created = await this.integrationService.createAssistant(this.integrationId, input).toPromise();
        this.currentAssistantId = created.id;
        this.assistant = created;
        this.isNew = false;
        this.alertService.success(this.t('agents.openaiAssistantForm.createSuccess'));
      } else {
        this.assistant = await this.integrationService.updateAssistant(this.integrationId, this.currentAssistantId, input).toPromise();
        this.needsSetup = false;
        this.alertService.success(this.t('agents.openaiAssistantForm.updateSuccess'));
      }

      const max = Math.max(this.steps.length - 1, 0);
      if (this.clampedIndex() < max) {
        this.nextStep();
      } else {
        this.router.navigate([`/agents/openai/${this.integrationId}/assistants`]);
      }
    } catch (error: any) {
      this.alertService.error(error?.error?.message ?? this.t('agents.openaiAssistantForm.saveAssistantError'));
    } finally {
      this.loading = false;
    }
  }

  previousStep(): void {
    this.stepIndex.update(index => Math.max(index - 1, 0));
  }

  get currentStep(): number {
    return this.currentStepSignal();
  }

  get progressPct(): number {
    return this.progressSignal();
  }

  get counter(): string {
    return this.t('agents.openaiAssistantForm.stepCounter', { current: this.currentStep, total: this.steps.length });
  }

  private clampedIndex = computed(() => {
    const total = this.steps.length;
    return total ? Math.min(Math.max(this.stepIndex(), 0), total - 1) : 0;
  });

  private currentStepSignal = computed(() => this.clampedIndex() + 1);
  private progressSignal = computed(() => {
    const total = this.steps.length;
    return total > 1 ? (this.clampedIndex() / (total - 1)) * 100 : 0;
  });

  cancel() {
    this.router.navigate([`/agents/openai/${this.integrationId}/assistants`]);
  }
}
